import { useMemo } from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useIxcRegistrationV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-account-query-service-account-get';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import type { IxcRegistrationV1AccountState } from '@interxfi/types/generated/api';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../components/router/router-tabbed-navigation';
import {
  getAccountRegistration,
  getAccountRegistrationTypeHasBeneficiaries,
} from '../../../../../../../../../features/registration/account/account-registration-utils';
import { EditAccountBeneficiariesDrawer } from '../../../../../../../../../features/registration/account/edit-account-beneficiaries-drawer';
import {
  getAccountRegistrationListItems,
  type getRegistrationAccountBeneficiariesListItems,
  type getRegistrationAccountBeneficiaryListItems,
} from '../../../../../../../../../features/registration/account/registration-account-list-items';
import { RegistrationAccountOverviewTabbedNavigation } from '../../../../../../../../../features/registration/account/registration-account-overview-tabbed-navigation';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_overview/detail/beneficiaries')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-${params.accountId}-beneficiaries`,
        label: 'Account Details',
        to: `/registration/account/${params.accountId}/detail/beneficiaries`,
      }),
    }) satisfies AggregateBreadcrumb,
  staticData: {
    tab: {
      group: TabbedNavigationGroup.RegistrationAccountOverview,
      label: 'Beneficiaries',
      order: 1,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.RegistrationAccountOverview>,
  head: () => getPageHead('Account Beneficiaries'),
  component: AccountBeneficiaryDetails,
});

const emptyStableRef = Object.freeze([]) as unknown as ReadonlyListItem[];

function buildBeneficiaryItemGroup(
  index: number,
  role: 'Primary' | 'Contingent',
  beneficiary: ReturnType<typeof getRegistrationAccountBeneficiaryListItems>,
): ReadonlyListGroup {
  return {
    id: `beneficiary-${role}-${index}`,
    items: [
      beneficiary.formatted,
      ...match(beneficiary.type)
        .with({ individual: P.not(P.nullish) }, (individualBeneficiary) => [
          individualBeneficiary.individual.value.name.formatted,
          individualBeneficiary.individual.value.birthDate,
          individualBeneficiary.individual.value.taxIdentifier,
          individualBeneficiary.individual.value.relationship,
        ])
        .with({ charity: P.not(P.nullish) }, (charityBeneficiary) => [
          charityBeneficiary.charity.value.legalName,
          ...(charityBeneficiary.charity.value.ein ? [charityBeneficiary.charity.value.ein] : []),
        ])
        .with({ trust: P.not(P.nullish) }, (trustBeneficiary) => [
          trustBeneficiary.trust.value.legalName,
          ...(trustBeneficiary.trust.value.ein ? [trustBeneficiary.trust.value.ein] : []),
        ])
        .otherwise(() => []),
    ],
  };
}

function getAccountBeneficiaryItemGroups(items: ReturnType<typeof getRegistrationAccountBeneficiariesListItems> | undefined): ReadonlyListGroup[] {
  const beneficiaryGroups: ReadonlyListGroup[] = [];

  items?.primary?.forEach((beneficiary, index) => {
    beneficiaryGroups.push(buildBeneficiaryItemGroup(index, 'Primary', beneficiary));
  });

  items?.contingent?.forEach((beneficiary, index) => {
    beneficiaryGroups.push(buildBeneficiaryItemGroup(index, 'Contingent', beneficiary));
  });

  return beneficiaryGroups;
}

function getListItems(state: IxcRegistrationV1AccountState | undefined) {
  const registration = getAccountRegistration(state?.data);
  const registrationItems = getAccountRegistrationListItems(registration);

  return match(registrationItems)
    .returnType<ReadonlyListGroup[]>()
    .with({ individual: { value: { transferOnDeath: { beneficiaries: P.select() } } } }, (items) => getAccountBeneficiaryItemGroups(items))
    .with(
      { joint: { value: { rights: { jointTenantsWithRightsOfSurvivorship: { value: { transferOnDeath: { beneficiaries: P.select() } } } } } } },
      (items) => getAccountBeneficiaryItemGroups(items),
    )
    .with({ ira: { value: { beneficiaries: P.select() } } }, (items) => getAccountBeneficiaryItemGroups(items))
    .with({ rothIra: { value: { beneficiaries: P.select() } } }, (items) => getAccountBeneficiaryItemGroups(items))
    .with({ sepIra: { value: { beneficiaries: P.select() } } }, (items) => getAccountBeneficiaryItemGroups(items))
    .with({ simpleIra: { value: { beneficiaries: P.select() } } }, (items) => getAccountBeneficiaryItemGroups(items))
    .otherwise(() => []);
}

function AccountBeneficiaryDetails() {
  const { accountId } = Route.useParams();
  const { data, isPending, error } = useIxcRegistrationV1AccountQueryServiceAccountGet({ accountId });
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.account), [data?.account]);
  const registration = getAccountRegistration(data?.account?.data);

  // If the account registration type does not have beneficiaries, redirect to the account overview page
  if (registration && !getAccountRegistrationTypeHasBeneficiaries(registration)) {
    return <Navigate params={{ accountId }} to="/registration/account/$accountId/detail" />;
  }

  return (
    <VerticalStack gap={7}>
      <RegistrationAccountOverviewTabbedNavigation
        accountId={accountId}
        actions={
          isPending ? undefined : (
            <RequiresAccess write>
              <EditAccountBeneficiariesDrawer
                accountId={accountId}
                trigger={
                  !listItems.length ? (
                    <Button affixIcon={<PlusIcon aria-hidden />} aria-label="Add beneficiaries" size="small" />
                  ) : (
                    <Button size="small">Edit</Button>
                  )
                }
              />
            </RequiresAccess>
          )
        }
      />

      {showPending || !listItems.length ? (
        <AppReadonlyList isPending={showPending} items={emptyStableRef} loadingRows={5} renderWhenEmpty="No Beneficiaries" />
      ) : (
        <AppReadonlyListStack gap={4} groups={listItems} isPending={showPending} />
      )}
    </VerticalStack>
  );
}
